/**
 * Account-role picker. Two ways to use it:
 *
 *  - **Mandatory gate** — pass only `isOpen`. No close button, no backdrop dismiss,
 *    which is what a post-login "you must choose" screen needs.
 *  - **On-demand switch** — pass `onClose` as well. The user can back out, and
 *    `title`/`subtitle` can be overridden so the caller asks its own question
 *    rather than the generic one.
 *
 * `onSelect` fires after the role is set, so a caller can continue whatever action
 * the old role was blocking.
 */

import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { FiX } from "react-icons/fi";
import { useAccountRole } from "../context/RoleContext";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import useEscapeKey from "../hooks/useEscapeKey";
import useDialogFocus from "../hooks/useDialogFocus";

const OPTIONS = [
  {
    role: "employer",
    emoji: "🏢",
    titleKey: "roleSelect.employerTitle",
    title: "Ажил олгогч",
    descKey: "roleSelect.employerDesc",
    desc: "Ажлын байрны зар нийтлэнэ",
  },
  {
    role: "jobseeker",
    emoji: "🧑‍💼",
    titleKey: "roleSelect.jobseekerTitle",
    title: "Ажил горилогч",
    descKey: "roleSelect.jobseekerDesc",
    desc: "Ажлын групп дээр пост оруулна",
  },
];

export default function RoleSelectModal({ isOpen, onClose, onSelect, title, subtitle }) {
  const { t } = useTranslation();
  const { setAccountRole } = useAccountRole();

  const dismissible = typeof onClose === "function";

  useBodyScrollLock(isOpen);
  // Escape is a dismissal like any other, so the mandatory gate has to suppress it too.
  useEscapeKey(isOpen && dismissible, onClose);
  const panelRef = useDialogFocus(isOpen);

  const choose = (role) => {
    setAccountRole(role);
    onSelect?.(role);
    onClose?.();
  };

  // Portaled to document.body: rendered inline from Header it sat inside Layout's
  // scroll-linked header wrapper, whose `fixed z-30` capped this overlay under the
  // sidebar (z-40) and the mobile bottom nav (z-50).
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="role-select-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        >
          {/* Backdrop dismisses only when the caller offered a way out. */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={dismissible ? onClose : undefined}
          />

          {/* Centred at every width, not a bottom sheet on the phone: this is a question
              that has to be answered before anything else happens, and a sheet rising from
              the edge reads as an optional drawer you can flick away. */}
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-select-title"
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
            transition={{ type: "spring", stiffness: 340, damping: 28 }}
            className="relative w-full max-w-[440px] md:w-[520px] md:max-w-[520px] bg-white rounded-3xl shadow-2xl px-6 pt-9 pb-7 md:px-9 md:pt-11 md:pb-9 focus:outline-none"
          >
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                aria-label={t("common.close", "Хаах")}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <FiX size={18} className="text-slate-600" />
              </button>
            )}

            <h2 id="role-select-title" className="text-[22px] md:text-[26px] font-bold text-center text-[#020618] leading-snug px-2">
              {title || t("roleSelect.title", "Та аль нь вэ?")}
            </h2>
            <p className="mt-2.5 text-center text-sm md:text-base text-slate-400">
              {subtitle || t("roleSelect.subtitle", "Танд тохирсон онцлогийг харуулахын тулд сонгоно уу.")}
            </p>

            <div className="mt-8 flex flex-col gap-3">
              {OPTIONS.map((opt) => (
                <button
                  key={opt.role}
                  type="button"
                  onClick={() => choose(opt.role)}
                  className="w-full flex items-center gap-4 text-left rounded-2xl border border-slate-200 px-5 py-4 hover:border-[#020618] transition-colors"
                >
                  <span className="text-[28px] leading-none flex-none">{opt.emoji}</span>
                  <span className="min-w-0">
                    <span className="block text-[15px] md:text-base font-bold text-[#020618]">
                      {t(opt.titleKey, opt.title)}
                    </span>
                    <span className="block mt-0.5 text-[12.5px] md:text-sm text-slate-400">
                      {t(opt.descKey, opt.desc)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
